'use client';
/**
 * 광고 견적서 요청 폼(ads.quote_v1) — 배치는 안내 페이지 레인이 정한다. 금액은 이 화면에 나오지 않는다(메일·견적서 열람 주소에서만).
 *  입력: 상품(복수) · 기간 · 대상(현장 검색 / 시군구 고르기) · 회사명/이름 · 이메일 · 사업자등록번호(선택) · 연락처(선택) · 개인정보 수집 동의(필수)
 *  보냄: POST /api/ads/quote-request. 스위치가 꺼져 있으면 서버가 404 QUOTE_OFF → 안내 문장.
 * ⛔ 가격 숫자를 번들에 싣지 않도록 lib/ads/defaults·pricing 을 import 하지 않는다.
 */
import { useEffect, useState } from 'react';
import { trackAdsFunnel } from './funnel-track';

type Product = 'site' | 'district' | 'home';
const PRODUCTS: { key: Product; label: string; desc: string }[] = [
  { key: 'site', label: '현장 광고', desc: '그 현장 상세페이지에 나갑니다.' },
  { key: 'district', label: '시군구 배너', desc: '그 시군구의 현장·글·모아보기 페이지에 나갑니다.' },
  { key: 'home', label: '홈 배너', desc: '카더라 홈에 나갑니다.' },
];
export const QUOTE_PRIVACY_NOTICE =
  '견적서 발송을 위해 회사명(이름)·이메일·사업자등록번호(선택)·연락처(선택)를 수집합니다. 견적 안내와 문의 응대에만 쓰며, 요청일로부터 1년 보관 후 지웁니다. 동의하지 않으면 견적서를 받을 수 없습니다.';

const box: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14, padding: 16, border: '1px solid var(--border, #CBD5E1)', borderRadius: 14, background: 'var(--bg-surface, transparent)', color: 'var(--text-primary, inherit)' };
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, fontWeight: 700 };
const inp: React.CSSProperties = { minHeight: 44, padding: '0 12px', borderRadius: 10, border: '1px solid var(--border, #CBD5E1)', background: 'var(--bg-base, transparent)', color: 'inherit', fontSize: 16 };
const chip = (on: boolean): React.CSSProperties => ({ minHeight: 44, padding: '0 14px', borderRadius: 999, border: `2px solid ${on ? 'var(--brand, #1E293B)' : 'var(--border, #CBD5E1)'}`, background: 'transparent', color: 'inherit', fontSize: 15, fontWeight: on ? 800 : 500, cursor: 'pointer' });
const sub: React.CSSProperties = { margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--text-secondary, inherit)' };

export interface QuoteRequestProps {
  /** 미리 고를 현장(현장 상세에서 열 때) */
  defaultSite?: { id: string; name: string } | null;
  /** 미리 고를 시군구 키(부산:해운대구) */
  defaultDistrict?: string | null;
  defaultProducts?: Product[];
}

export default function QuoteRequest({ defaultSite = null, defaultDistrict = null, defaultProducts }: QuoteRequestProps) {
  const [products, setProducts] = useState<Product[]>(defaultProducts ?? (defaultSite ? ['site'] : defaultDistrict ? ['district'] : []));
  const [weeks, setWeeks] = useState<2 | 4>(2);
  const [sites, setSites] = useState<{ id: string; name: string }[]>(defaultSite ? [defaultSite] : []);
  const [districts, setDistricts] = useState<string[]>(defaultDistrict ? [defaultDistrict] : []);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ id: string; name: string; area: string; blocked: string | null }[]>([]);
  const [allDistricts, setAllDistricts] = useState<{ key: string; region: string; sigungu: string; blocked: string | null }[] | null>(null);
  const [form, setForm] = useState({ company: '', contactName: '', email: '', bizNo: '', phone: '', website: '' });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [done, setDone] = useState<{ quoteNo: string; path: string; emailed: boolean } | null>(null);

  const has = (p: Product) => products.includes(p);
  const toggle = (p: Product) => setProducts((xs) => (xs.includes(p) ? xs.filter((x) => x !== p) : [...xs, p]));

  useEffect(() => {
    if (!has('site') || q.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/ads/targets?q=${encodeURIComponent(q.trim())}`).then((x) => x.json());
        setFound(Array.isArray(r?.sites) ? r.sites : []);
      } catch { setFound([]); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, products]);

  useEffect(() => {
    if (!has('district') || allDistricts) return;
    fetch('/api/ads/targets?list=districts').then((x) => x.json()).then((r) => setAllDistricts(Array.isArray(r?.districts) ? r.districts : [])).catch(() => setAllDistricts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!consent) { setMsg('개인정보 수집·이용에 동의해 주세요.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/ads/quote-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products, weeks, sites: sites.map((s) => s.id), districts, ...form, privacyConsent: consent }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) setMsg(j?.code === 'QUOTE_OFF' ? '지금은 견적서 자동 발행을 하지 않습니다.' : j?.error || '견적을 만들지 못했습니다.');
      else {
        trackAdsFunnel('quote_request', { products: products.join(','), weeks });
        setDone({ quoteNo: j.quoteNo, path: j.path, emailed: !!j.emailed });
      }
    } catch {
      setMsg('연결이 끊겼습니다. 잠시 뒤 다시 요청해 주세요.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section style={box} aria-live="polite">
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>견적서를 발행했습니다</h3>
        <p style={{ ...sub, fontSize: 16 }}>견적 번호 <b>{done.quoteNo}</b> · {done.emailed ? '입력한 이메일로 보냈습니다.' : '메일 발송이 늦어질 수 있어 아래에서 바로 볼 수 있습니다.'}</p>
        <a href={done.path} style={{ ...chip(true), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>견적서 보기·인쇄</a>
        <p style={sub}>견적은 7일 동안 유효하며, 자리와 가격은 결제할 때 확정됩니다.</p>
      </section>
    );
  }

  return (
    <form style={box} onSubmit={submit} aria-label="광고 견적서 요청">
      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>견적서 받기</h3>
      <p style={sub}>상품·기간·대상을 고르면 견적서를 바로 메일로 보냅니다. 견적은 7일 동안 유효하고, 자리와 가격은 결제할 때 확정됩니다.</p>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <legend style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>상품(여러 개 고를 수 있음)</legend>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PRODUCTS.map((p) => (
            <button key={p.key} type="button" aria-pressed={has(p.key)} style={chip(has(p.key))} onClick={() => toggle(p.key)} title={p.desc}>{p.label}</button>
          ))}
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>기간</legend>
        <div style={{ display: 'flex', gap: 8 }}>
          {([2, 4] as const).map((w) => (
            <button key={w} type="button" aria-pressed={weeks === w} style={chip(weeks === w)} onClick={() => setWeeks(w)}>{w}주</button>
          ))}
        </div>
      </fieldset>

      {has('site') && (
        <div style={lab}>
          현장
          <input style={inp} value={q} onChange={(e) => setQ(e.target.value)} placeholder="현장 이름 2글자 이상" aria-label="현장 검색" />
          {found.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {found.slice(0, 8).map((s) => (
                <button key={s.id} type="button" disabled={!!s.blocked} style={{ ...chip(false), borderRadius: 10, textAlign: 'left', opacity: s.blocked ? 0.6 : 1 }}
                  onClick={() => { setSites((xs) => (xs.some((x) => x.id === s.id) ? xs : [...xs, { id: s.id, name: s.name }].slice(0, 10))); setQ(''); }}>
                  {s.name} <span style={{ fontWeight: 400, fontSize: 13 }}>{s.area}{s.blocked ? ` · ${s.blocked}` : ''}</span>
                </button>
              ))}
            </div>
          )}
          {sites.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {sites.map((s) => <button key={s.id} type="button" style={chip(true)} onClick={() => setSites((xs) => xs.filter((x) => x.id !== s.id))} aria-label={`${s.name} 빼기`}>{s.name} ✕</button>)}
            </div>
          )}
        </div>
      )}

      {has('district') && (
        <label style={lab}>
          시군구
          <select style={inp} value="" onChange={(e) => { const k = e.target.value; if (k) setDistricts((xs) => (xs.includes(k) ? xs : [...xs, k].slice(0, 60))); }}>
            <option value="">{allDistricts ? '시군구 고르기' : '불러오는 중…'}</option>
            {(allDistricts ?? []).filter((d) => !d.blocked).map((d) => <option key={d.key} value={d.key}>{d.region} {d.sigungu}</option>)}
          </select>
          {districts.length > 0 && (
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {districts.map((k) => <button key={k} type="button" style={chip(true)} onClick={() => setDistricts((xs) => xs.filter((x) => x !== k))} aria-label={`${k.replace(':', ' ')} 빼기`}>{k.replace(':', ' ')} ✕</button>)}
            </span>
          )}
        </label>
      )}

      <label style={lab}>회사명 또는 이름<input style={inp} required maxLength={60} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} autoComplete="organization" /></label>
      <label style={lab}>담당자(선택)<input style={inp} maxLength={30} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} autoComplete="name" /></label>
      <label style={lab}>견적서를 받을 이메일<input style={inp} required type="email" maxLength={200} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" /></label>
      <label style={lab}>사업자등록번호(선택)<input style={inp} inputMode="numeric" maxLength={20} value={form.bizNo} onChange={(e) => setForm({ ...form, bizNo: e.target.value })} placeholder="숫자 10자리" /></label>
      <label style={lab}>연락처(선택 · 견적서에 싣지 않음)<input style={inp} type="tel" inputMode="tel" maxLength={20} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="tel" /></label>
      <input tabIndex={-1} aria-hidden="true" autoComplete="off" style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} name="website" />

      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.55 }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ width: 22, height: 22, marginTop: 2 }} required />
        <span><b>[필수] 개인정보 수집·이용 동의</b> — {QUOTE_PRIVACY_NOTICE}</span>
      </label>

      {msg && <p role="alert" style={{ ...sub, color: 'var(--danger, #B91C1C)', fontWeight: 700 }}>{msg}</p>}
      <button type="submit" disabled={busy || !products.length} style={{ ...chip(true), borderRadius: 12, minHeight: 50, fontSize: 17 }}>
        {busy ? '견적서 만드는 중…' : '견적서 메일로 받기'}
      </button>
    </form>
  );
}
